import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { LifeBuoy, ArrowRight, PlusCircle } from "lucide-react";

const STATUS_COLORS = {
  open: "bg-emerald-100 text-emerald-700 border-emerald-300",
  in_review: "bg-blue-100 text-blue-700 border-blue-300",
  awaiting_user: "bg-amber-100 text-amber-700 border-amber-300",
  resolved: "bg-slate-100 text-slate-600 border-slate-300",
  closed: "bg-slate-100 text-slate-500 border-slate-300",
};
const PRIORITY_COLORS = {
  low: "text-slate-500", normal: "text-blue-600", high: "text-red-600",
};

export default function MyTickets() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/support/tickets/mine")
      .then(({ data }) => setTickets(data.tickets || []))
      .catch(() => { /* toast handled by interceptor */ })
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-slate-50 py-10">
      <div className="max-w-3xl mx-auto px-4 md:px-8">
        <div className="hw-label text-blue-700">Support · My tickets</div>
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mt-2">
          <h1 className="hw-head text-3xl md:text-4xl font-black text-slate-900 flex items-center gap-2" data-testid="my-tickets-heading">
            <LifeBuoy className="w-7 h-7 text-emerald-600" /> Your support tickets
          </h1>
          <Link to="/support/new" className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm font-bold px-4 py-2" data-testid="new-ticket-btn">
            <PlusCircle className="w-4 h-4" /> New ticket
          </Link>
        </div>

        <div className="mt-6 space-y-3" data-testid="tickets-list">
          {loading ? (
            <div className="hw-card p-6 text-center text-slate-500 text-sm">Loading…</div>
          ) : tickets.length === 0 ? (
            <div className="hw-card p-8 text-center">
              <div className="hw-head text-lg font-black text-slate-700">No tickets yet</div>
              <p className="text-sm text-slate-500 mt-2">Good — hopefully you never need us. If you do, we&apos;re here.</p>
              <Link to="/support" className="mt-4 inline-flex text-blue-700 hover:underline text-sm">Back to Support Centre →</Link>
            </div>
          ) : tickets.map((t) => (
            <Link key={t.ticket_id} to={`/support/ticket/${t.ticket_id}`}
              className="hw-card p-4 flex items-start gap-3 hover:hw-shadow-pop-amber transition-shadow"
              data-testid={`ticket-row-${t.ticket_id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`hw-chip border ${STATUS_COLORS[t.status] || STATUS_COLORS.open} capitalize`}>{t.status.replace('_', ' ')}</span>
                  <span className={`text-xs font-bold uppercase ${PRIORITY_COLORS[t.priority] || ""}`}>{t.priority}</span>
                  <span className="text-xs text-slate-400 capitalize">· {t.category}</span>
                </div>
                <div className="hw-head font-black text-slate-900 mt-2 truncate">{t.subject}</div>
                <div className="hw-mono text-[11px] text-slate-500 mt-1">{t.ticket_id} · Created {new Date(t.created_at).toLocaleString()}</div>
              </div>
              <ArrowRight className="w-5 h-5 text-slate-400 mt-1" />
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
